import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, Phone, Send, Copy, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [stars, setStars] = useState([]);
  
  const readingData = location.state?.readingData;
  const readingType = location.state?.readingType;

  useEffect(() => {
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 100; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("hotonbaptiste838@gmail.com");
    toast.success(t('email_copied'));
  };

  const handleSendSMS = () => {
    // Support for both iOS and Android
    const phoneNumber = "0644792766";
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      window.open(`sms:${phoneNumber}&body=`, '_self');
    } else {
      window.open(`sms:${phoneNumber}?body=`, '_self');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('error_required_fields') || "Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
        reading_id: readingData?.id || null,
        reading_type: readingType || null
      });

      toast.success(t('success_contact') || "Message sent successfully!");
      setFormData({ name: "", email: "", phone: "", message: "" });
      
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || t('error_generic') || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative pb-12">
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <Button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('btn_back')}
          </Button>
          <img
            src="/elua-logo.png"
            alt="ELUA"
            className="h-16 w-16 object-contain logo-pulse"
          />
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-4">
            <Mail className="w-10 h-10 golden-accent" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 glow-text" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('contact_title')}
          </h1>
          <p className="text-xl text-white/80">
            {t('contact_subtitle')}
          </p>
        </div>

        {/* Direct Contact Section - Masked for privacy */}
        <Card className="glass-card max-w-2xl mx-auto border-2 golden-border mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('contact_direct_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Email - Masked */}
            <div className="bg-white/5 rounded-lg p-6 border border-[#F4C430]/30">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-[#F4C430] font-semibold mb-2">{t('contact_email_label')}</p>
                  <p className="text-white text-lg">hoton••••••••@gmail.com</p>
                </div>
                <Button
                  onClick={handleCopyEmail}
                  className="bg-white/10 hover:bg-white/20 border border-[#F4C430]/50 text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('btn_copy_email')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card max-w-2xl mx-auto border-2 golden-border card-entrance" data-testid="contact-form">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('contact_form_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-white/90 text-base mb-2 block">
                  {t('label_fullname')} *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  type="text"
                  placeholder={t('label_fullname')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white/90 text-base mb-2 block">
                  {t('label_email')} *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    placeholder={t('label_email')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430] pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-white/90 text-base mb-2 block">
                  {t('label_phone')}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder={t('label_phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430] pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-white/90 text-base mb-2 block">
                  {t('label_message')} *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  placeholder={t('placeholder_message')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430] min-h-[150px]"
                  required
                />
              </div>

              <Button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold py-6 text-lg"
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                <Send className="w-5 h-5 mr-2" />
                {loading ? t('btn_sending') : t('btn_send')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
